import {
  ChipContactHistoryEntry,
  ChipFullContactInfoProps,
  ChipRedactedContactInfoProps,
  ChipSearchResult,
} from '@barkinglabs/nano-shared';
import { CommonChip } from '../../../lib/types';
import ContactModal, { searchResultToContactModalProps } from '../../modals/ContactModal/ContactModal';
import styles from '../PetDetails/PetDetails.module.scss';
import { ChipContactInfo } from '../../../../types/gql-op-types';
import InfoCard from '../../InfoCard/InfoCard';
import { AAHASearchUrl } from '@/lib/constants';

export type ContactInfo = ChipRedactedContactInfoProps | ChipFullContactInfoProps | ChipContactInfo;

const getName = (contactInfo: ContactInfo) => {
  let name = contactInfo.firstName ?? '';
  if ('lastInitial' in contactInfo) {
    name += ` ${contactInfo.lastInitial}.`;
  } else if ('lastName' in contactInfo) {
    name += ` ${contactInfo.lastName ?? ''}`.trimEnd();
  }

  return name;
};

const getFormattedAddress = (address: ChipContactInfo): React.ReactElement | null => {
  if (!address.line1 && !address.city && !address.state && !address.zipcode) {
    return null;
  }

  return (
    <span>
      {address.line1 && (
        <>
          {address.line1}
          <br />
        </>
      )}
      {address.city} {address.state} {address.zipcode}
    </span>
  );
};

const getEmail = (contactInfo: ContactInfo) => {
  let email;
  if ('primaryEmail' in contactInfo) {
    email = contactInfo.primaryEmail;
  } else if ('obfuscatedEmail' in contactInfo) {
    email = contactInfo.obfuscatedEmail;
  }

  return email ?? 'No data';
};

const getPhone = (contactInfo: ContactInfo) => {
  let phone;
  if ('primaryPhone' in contactInfo) {
    phone = contactInfo.primaryPhone;
  } else if ('obfuscatedEmail' in contactInfo) {
    phone = contactInfo.obfuscatedPhone;
  }

  return phone ?? 'No data';
};

// For Chip Search:
//   - verified rescue users: if primaryEmail is available show full unredacted contact info
//   - all other users: show only redacted contact info
export const getContactInfo = (contactDetails?: ChipContactHistoryEntry[] | ContactInfo) => {
  let contactInfo, email, ownerName, phone, address, city;
  let contactId = '';
  if (Array.isArray(contactDetails)) {
    contactInfo = contactDetails?.find((entry) => {
      contactId = entry.id;
      return entry.contactInfo;
    })?.contactInfo;
  } else {
    contactInfo = contactDetails;
  }

  if (contactInfo) {
    ownerName = getName(contactInfo);
    email = getEmail(contactInfo);
    phone = getPhone(contactInfo);
  }

  if (contactInfo && 'primaryEmail' in contactInfo) {
    address = getFormattedAddress(contactInfo);
  } else if (contactInfo) {
    city = contactInfo.city;
  }

  return { contactId, ownerName, email, phone, address, city };
};

interface ContactDetailsProps {
  chip?: Pick<CommonChip, 'serialNumber'>;
  contactInfo?: ContactInfo;
  searchResult?: ChipSearchResult | null;
  status?: 'active' | 'inactive';
  canRecallAsReseller?: boolean;
  onRecallClicked?: () => void;
}

export default function ContactDetails({
  chip,
  contactInfo,
  searchResult,
  canRecallAsReseller,
  onRecallClicked,
}: ContactDetailsProps) {
  const contactInfoAccessor = searchResult?.contactHistory ?? contactInfo;
  const { contactId, ownerName, email, phone, address, city } = getContactInfo(contactInfoAccessor);
  const serialNumber = chip ? chip.serialNumber : searchResult?.chip.serialNumber;
  const externalRegistriesSearchUrl = AAHASearchUrl(serialNumber);

  const hasContactInfo = ownerName || email;

  const Header = (
    <>
      <div className={styles.header}>
        <div className={styles.petName}>{ownerName || 'Owner name not available'}</div>
        <div className={styles.lastUpdated}>Owner Information</div>
      </div>
      {serialNumber && (
        <div className={styles.chipId}>
          <div className={styles.chipIdLabel}>Chip ID</div>
          <div>{serialNumber}</div>
        </div>
      )}
    </>
  );

  return (
    <div className={styles.main}>
      {canRecallAsReseller && (
        <button className={styles.topRight} onClick={onRecallClicked}>
          Clear Owner
        </button>
      )}
      {hasContactInfo ? (
        <>
          <InfoCard
            /* If it's the public search result page, don't show the header */
            header={Header}
            rows={[
              [
                {
                  label: 'Owner',
                  value: `${ownerName ? ownerName : 'Name not available'}${email ? ' (' + email + ')' : ''}`,
                },
              ],
              [{ label: 'Phone', value: phone ?? 'No data' }],
              address ? [{ label: 'Address', value: address }] : [{ label: 'City', value: city || 'No data' }],
            ]}
          />
          {searchResult && (
            <div className={styles.buttons}>
              <ContactModal
                trigger={<button className="btn-black">Send Message</button>}
                {...searchResultToContactModalProps(searchResult)}
                contactTarget={{
                  kind: 'contactHistory',
                  id: contactId,
                }}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <p>No data found for pet or owner in Fi Nano Registry</p>
          <button className="btn-black">
            <a href={externalRegistriesSearchUrl} target="_blank">
              Check other registries{' '}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 19H5V5H12V3H3V21H21V12H19V19ZM14 3V5H17.59L7.76 14.83L9.17 16.24L19 6.41V10H21V3H14Z"></path>
              </svg>
            </a>
          </button>
          {searchResult?.chip.isFiChip && (
            <button className="btn-black" style={{ marginTop: '12px' }}>
              <a href={`/register?id=${serialNumber}`}>Register this chip</a>
            </button>
          )}
        </>
      )}
    </div>
  );
}
